// Incremental demuxer for the server's multipart audio stream (audioResponseSender.ts).
// Each segment arrives as "--peech_stream_boundary\r\n" + "Content-Type: <type>\r\n\r\n" +
// body, with NO closing "--…--" delimiter and NO \r\n after bodies — the stream just ends.
// audio/mpeg bodies concatenated in arrival order are the complete MP3; application/json
// bodies each hold one SpeechMarkup. The server's three writes per segment give TCP free
// rein to split a chunk anywhere — mid-boundary, mid-header, mid-JSON, mid-audio — so every
// state here survives an arbitrary split (the iOS MultipartStreamProcessor has a TODO for
// the boundary/header splits; this parser closes it). MP3 bytes could in principle contain
// the boundary string; the server calls it "unique enough" and we accept that risk.

import { SpeechMarkup, isSpeechMarkup } from 'application/src/app/features/player/speech-markup';

export interface DemuxResult {
  audio: Uint8Array[];
  markups: SpeechMarkup[];
}

type State = 'preamble' | 'header' | 'body';
type BodyKind = 'audio' | 'json' | 'drop';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

const BOUNDARY = encoder.encode('--peech_stream_boundary');
const HEADER_END = encoder.encode('\r\n\r\n');
const EMPTY = new Uint8Array(0);

export class MultipartDemuxer {
  // Unconsumed bytes. Audio is drained out of it eagerly, so it only ever holds a partial
  // token/header, a JSON body in progress, or an audio tail short enough to hide a boundary.
  private pending: Uint8Array = EMPTY;
  // Index into pending below which the current state's needle is known not to start —
  // keeps repeated pushes from re-scanning an accumulating body quadratically.
  private scanFrom = 0;
  private state: State = 'preamble';
  private bodyKind: BodyKind = 'drop';

  push(chunk: Uint8Array): DemuxResult {
    const result: DemuxResult = { audio: [], markups: [] };

    if (chunk.length) {
      this.pending = this.pending.length ? concatBytes(this.pending, chunk) : chunk;
      this.drain(result);
    }

    return result;
  }

  flush(): DemuxResult {
    const result: DemuxResult = { audio: [], markups: [] };

    if (this.state === 'body' && this.pending.length) {
      if (this.bodyKind === 'audio') {
        result.audio.push(this.pending.slice());
      } else if (this.bodyKind === 'json') {
        this.parseMarkup(this.pending, result);
      }
    }
    // A truncated preamble/boundary/header at EOF has nothing usable in it — drop.

    this.pending = EMPTY;
    this.scanFrom = 0;
    this.state = 'preamble';

    return result;
  }

  private drain(result: DemuxResult): void {
    while (true) {
      if (this.state === 'preamble') {
        const at = indexOfBytes(this.pending, BOUNDARY, this.scanFrom);

        if (at < 0) {
          // Drop preamble bytes that can no longer start a boundary; keep a possible
          // partial token for the next chunk.
          this.consume(this.pending.length - (BOUNDARY.length - 1));
          return;
        }

        this.consume(at + BOUNDARY.length);
        this.state = 'header';
      } else if (this.state === 'header') {
        const at = indexOfBytes(this.pending, HEADER_END, this.scanFrom);

        if (at < 0) {
          this.scanFrom = Math.max(0, this.pending.length - (HEADER_END.length - 1));
          return;
        }

        const header = decoder.decode(this.pending.subarray(0, at));
        this.bodyKind = header.includes('audio/mpeg')
          ? 'audio'
          : header.includes('application/json')
            ? 'json'
            : 'drop';
        this.consume(at + HEADER_END.length);
        this.state = 'body';
      } else {
        const at = indexOfBytes(this.pending, BOUNDARY, this.scanFrom);

        if (at < 0) {
          // No boundary yet. Audio can flow through eagerly — all but the trailing
          // boundaryLength-1 bytes, which could be the start of the next boundary.
          // JSON must accumulate whole (it is parsed in one piece at the boundary).
          const safe = this.pending.length - (BOUNDARY.length - 1);

          if (this.bodyKind === 'json') {
            this.scanFrom = Math.max(0, safe);
          } else if (safe > 0) {
            if (this.bodyKind === 'audio') {
              result.audio.push(this.pending.slice(0, safe));
            }
            this.consume(safe);
          }
          return;
        }

        if (this.bodyKind === 'audio') {
          if (at > 0) {
            result.audio.push(this.pending.slice(0, at));
          }
        } else if (this.bodyKind === 'json') {
          this.parseMarkup(this.pending.subarray(0, at), result);
        }

        this.consume(at + BOUNDARY.length);
        this.state = 'header';
      }
    }
  }

  private consume(count: number): void {
    if (count > 0) {
      this.pending = count >= this.pending.length ? EMPTY : this.pending.subarray(count);
    }
    this.scanFrom = 0;
  }

  private parseMarkup(bytes: Uint8Array, result: DemuxResult): void {
    try {
      const value: unknown = JSON.parse(decoder.decode(bytes));
      if (isSpeechMarkup(value)) {
        result.markups.push(value);
      }
      // A shape the guard rejects is a word the client can't use — drop silently.
    } catch {
      // Malformed JSON from the server is likewise dropped, matching iOS.
    }
  }
}

function concatBytes(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length + b.length);
  out.set(a);
  out.set(b, a.length);
  return out;
}

function indexOfBytes(haystack: Uint8Array, needle: Uint8Array, from: number): number {
  const last = haystack.length - needle.length;
  const first = needle[0];

  for (let i = from > 0 ? from : 0; i <= last; i++) {
    if (haystack[i] !== first) {
      continue;
    }

    let j = 1;
    while (j < needle.length && haystack[i + j] === needle[j]) {
      j++;
    }
    if (j === needle.length) {
      return i;
    }
  }

  return -1;
}
